import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuthStore } from '@/features/auth/store';
import { useLogout } from '@/features/auth/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { BYOKeysSection } from '@/features/settings/components/BYOKeysSection';
import { BYOLockedCard } from '@/features/settings/components/BYOLockedCard';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

export function SettingsPage() {
  usePaperTheme();
  const user = useAuthStore((s) => s.user);
  const { mutate: handleLogout, isPending } = useLogout();

  const currentPlan = user?.plan ?? 'free';
  const expiry = user?.plan_expiry ? new Date(user.plan_expiry) : null;
  const isPaid = currentPlan !== 'free' && (!expiry || expiry.getTime() > Date.now());

  return (
    <AppLayout>
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="mx-auto max-w-2xl space-y-6">
          <PageHeader
            title="Settings"
            description="Manage your account, plan, and AI keys."
            backTo="/dashboard"
          />

          <section className="rounded-clyra-lg border border-line-default bg-clyra-raised p-6">
            <h2 className="text-sm font-medium text-ink-primary">Account</h2>
            <p className="mt-2 text-sm text-ink-secondary">{user?.email}</p>
            <p className="mt-1 text-petit text-ink-muted">Role: {user?.role}</p>
          </section>

          <section className="rounded-clyra-lg border border-line-default bg-clyra-raised p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-sm font-medium text-ink-primary">Plan</h2>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm capitalize text-ink-secondary">{currentPlan}</span>
                  {isPaid && (
                    <span className="rounded-full border border-success/30 bg-success/10 px-1.5 py-0.5 text-micro text-success">
                      Active
                    </span>
                  )}
                </div>
                {expiry && isPaid && (
                  <p className="mt-1 text-petit text-ink-muted">
                    Renews {expiry.toLocaleDateString()}
                  </p>
                )}
                {!isPaid && (
                  <p className="mt-1 text-petit text-ink-muted">
                    Upgrade for higher daily limits and managed AI.
                  </p>
                )}
              </div>
              <Link to="/billing">
                <Button size="sm" variant={isPaid ? 'secondary' : 'primary'}>
                  {isPaid ? 'Manage' : 'Upgrade'}
                </Button>
              </Link>
            </div>
          </section>

          {currentPlan === 'byo' && isPaid ? <BYOKeysSection /> : <BYOLockedCard />}

          <section className="rounded-clyra-lg border border-line-default bg-clyra-raised p-6">
            <h2 className="text-sm font-medium text-ink-primary">Appearance</h2>
            <p className="mt-1 text-petit text-ink-muted">
              Choose how Clyra looks. System matches your OS setting.
            </p>
            <div className="mt-4">
              <ThemeToggle variant="labeled" />
            </div>
          </section>

          <section className="rounded-clyra-lg border border-line-default bg-clyra-raised p-6">
            <h2 className="text-sm font-medium text-ink-primary">Danger Zone</h2>
            <Button
              variant="danger"
              size="sm"
              className="mt-3"
              onClick={() => handleLogout()}
              loading={isPending}
            >
              Log out
            </Button>
          </section>
        </div>
      </main>
    </AppLayout>
  );
}
